/**
 * Voice type classification - Method 2: Typical Pitch Classification only
 */

export interface VoiceType {
  minFrequency: number;
  maxFrequency: number;
  minNote: string;
  maxNote: string;
  typicalRange: string;
}

export const VOICE_TYPES: Record<string, VoiceType> = {
  BASS: {
    minFrequency: 82,
    maxFrequency: 330,
    minNote: 'E2',
    maxNote: 'E4',
    typicalRange: 'E2-E4'
  },
  BARITONE: {
    minFrequency: 110,
    maxFrequency: 440,
    minNote: 'A2',
    maxNote: 'A4',
    typicalRange: 'A2-A4'
  },
  TENOR: {
    minFrequency: 131,
    maxFrequency: 523,
    minNote: 'C3',
    maxNote: 'C5',
    typicalRange: 'C3-C5'
  },
  ALTO: {
    minFrequency: 175,
    maxFrequency: 698,
    minNote: 'F3',
    maxNote: 'F5',
    typicalRange: 'F3-F5'
  },
  MEZZO_SOPRANO: {
    minFrequency: 220,
    maxFrequency: 880,
    minNote: 'A3',
    maxNote: 'A5',
    typicalRange: 'A3-A5'
  },
  SOPRANO: {
    minFrequency: 262,
    maxFrequency: 1047,
    minNote: 'C4',
    maxNote: 'C6',
    typicalRange: 'C4-C6'
  }
};

/**
 * Typical/characteristic pitch for each voice type
 * These represent the most common comfortable singing pitches
 */
const VOICE_TYPE_TYPICAL_PITCHES: Record<string, number> = {
  BASS: 82,           // E2
  BARITONE: 110,      // A2
  TENOR: 165,         // E3
  ALTO: 220,          // A3
  MEZZO_SOPRANO: 262, // C4
  SOPRANO: 330        // E4
};

export interface TypicalPitchClassification {
  key: string;
  voiceType: VoiceType;
  typicalPitch: number;
  distance: number;
}

/**
 * Classify voice type based on a single typical pitch (median of medians)
 * Finds the voice type whose characteristic pitch is closest to the given frequency
 */
export function classifyByTypicalPitch(frequency: number): TypicalPitchClassification | null {
  if (!frequency || frequency <= 0) {
    return null;
  }

  let bestMatch: TypicalPitchClassification | null = null;
  let bestDistance = Infinity;

  for (const [key, voiceType] of Object.entries(VOICE_TYPES)) {
    const typicalPitch = VOICE_TYPE_TYPICAL_PITCHES[key];
    if (typicalPitch === undefined) continue;

    // Use log2 distance for better pitch perception (measures in octaves)
    const distance = Math.abs(Math.log2(frequency / typicalPitch));

    if (distance < bestDistance) {
      bestDistance = distance;
      bestMatch = {
        key,
        voiceType,
        typicalPitch,
        distance
      };
    }
  }

  return bestMatch;
}
